import { Performer, T_TrackMetadata } from "@type/global.types";
import moment from "moment";
import { FilterFunc } from "rc-select/lib/Select";
import { Frontend } from "shared-types";
import dayjs from "dayjs";

const RANDOM_STRING_LOWER = 1;
const RANDOM_STRING_UPPER = 1 << 1;
const RANDOM_STRING_NUMBER = 1 << 2;
const RANDOM_STRING_SPECIAL = 1 << 3;

export enum Actions {
  add,
  delete,
  edit,
  detail,
  duplicate
}

export function friendlyDateTime(date: string) {
  return moment(date).format("DD.MM.YYYY hh:mm a");
}

export function friendlyDate(date: string = '') {
  if (date === null || date === undefined || date === '') return '';
  return moment(date).format("DD.MM.YYYY");
}

export function friendlyTimeBySecond(t: number) {
  const s = t % 60;
  t -= s;
  t /= 60;
  const m = t % 60;
  t -= m;
  const c = (n: number) => {
    if (n < 10) return '0' + n;
    return n;
  }
  return `${c(t / 60)}:${c(m)}:${c(s)}`
}

export function friendlyString(s: string, n: number, r: string = '') {
  let arr = s.split(' ');
  let res = '';
  if (s.length <= n) return s;
  if (r !== '') {
    return s.substring(0, n) + r;
  }
  for (var i = 0; i < arr.length; i++) {
    let r = res + (res === '' ? '' : ' ') + arr[i];
    if (r.length > n) return res;
    res = r;
  }
  return res;
}

export const randomString = (length: number = 32, mode: number = RANDOM_STRING_LOWER | RANDOM_STRING_UPPER) => {
  let chars = "";
  if (mode & RANDOM_STRING_LOWER) chars += "abcdefghijklmnopqrstuvwxyz";
  if (mode & RANDOM_STRING_UPPER) chars += "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  if (mode & RANDOM_STRING_NUMBER) chars += "0123456789";
  if (mode & RANDOM_STRING_SPECIAL) chars += "!@#$%^&($)|}{[].,<>";
  const len = chars.length;
  let res = '';
  for (var i = 0; i < length; i++) {
    res += chars.charAt(Math.floor(Math.random() * len));
  }
  return res;
}

/**
 * Stop enter submitting the form.
 * @param keyEvent Event triggered when the user presses a key.
 */
export function onKeyDownForm(keyEvent: any) {
  if ((keyEvent.charCode || keyEvent.keyCode) === 13) {
    keyEvent.preventDefault();
  }
}

export const filterOption: FilterFunc<Frontend.T_SelectItem | Frontend.T_SelectItem<string>> = (input: string, option?: Frontend.T_SelectItem | Frontend.T_SelectItem<string>) => {
  return !!(option?.label.toLocaleLowerCase() ?? '').includes(input.toLocaleLowerCase());
}

export const getSelectOptions = (arr: { id: number | string; title?: string }[]) => arr.map(item => ({ label: item.title || "", value: Number(item.id) }))

export const getSelectedOptions = (arr: { value: number | string; label?: string }[]) => arr.map(item => {
  return { label: item.label || "", value: Number(item.value) }
});

export const country = (name: string, code: string) => {
  const res: Frontend.Release.Country = {
    code,
    name,
  };
  return res;
};

export const fileToBlob = (file: File): Promise<Blob> => new Promise((resolve, reject) => {
  var reader = new FileReader();
  reader.onloadend = () => {
    if (reader.result && reader.result instanceof ArrayBuffer) {
      const blob = new Blob([reader.result], {
        type: file.type
      });
      resolve(blob);
    }
    else reject("file error");
  }
  reader.onerror = error => reject(`Error reading ${error}`);
  reader.readAsBinaryString(file);
})

export const round = (num: number, n: number = 2) => {
  const n10 = Math.pow(10, 2);
  let ret = Math.floor(num * n10) / n10;
  let retStr = ret.toString();
  const arr = retStr.split(".");
  while (!arr[1]?.length || arr[1]?.length < n) {
    if (arr.length === 1) {
      arr.push('0');
      continue;
    }
    arr[1] = `${arr[1]}0`;
  }
  return arr.join('.');
}


export const getPostDataFromFormData = (inData: T_TrackMetadata) => {
  let res: Frontend.T_TrackMetadataOutput = {
    track_title: inData.trackTitle,
    track_part: inData.trackPart,
    track_type: inData?.trackType?.value,
    instrumental: inData.instPossibleVal,
    isrc: inData.isrc,
    genre_id: inData.genre.value,
    fileunder_id: inData.subGenre.value,
    title_lang_id: inData.titleLanguage?.value,
    lyrics_lang_id: inData.language,
    publisher_id: inData.publisher_id,
    price_code: inData.priceCode,
    explicit_content: inData.explicitContent,
    circle_p: inData.circleP,
    publication_date: inData.yearOfPublication.format("YYYY-MM-DD"),
    catalogue_num: inData.catalogue_num,
    start_of_preview: inData.startOfPreview,
    release_date: inData.releaseDate.format("YYYY-MM-DD"),
    distribution_territories: inData.distributionTerr?.map((item) => {
      return item.value;
    }),
    lyrics: inData.lyrics,
    members:
      inData.performers?.map((performer: Performer) => ({
        member_title: performer.title,
        member_id: performer.id,
        role_id: performer.role,
        primary: performer.primary,
      })) || [],
    update_performers: inData.performers.length > 2 ? true : false,
    track_file: inData.trackFile,
    register_from: 2,
    temp_track_info: JSON.stringify(inData.tempTrackInfo),
    track_file_name: inData.trackFileName,
    version: inData.version,
    mood_id: inData.mood?.value,
    copyright_p: inData.copyright_p.valueOf(),
    copyright_c: inData.copyright_c,
    rec_date: inData.rec_date.format("YYYY-MM-DD"),
    rec_country_id: inData.rec_country_id,
    rec_city: inData.rec_city,
    source_cd: inData.source_cd,
    source: inData.source,
    licensor: inData.licensor.value

  };
  return res;
};

export const validateUrl = (url: string): boolean => {
  const urlRegex = /^(http|ftp|https):\/\/[^\s/$.?#].[^\s]*$/;
  return urlRegex.test(url);
};

export const isDateValid = (dateString: string) => {
  return dayjs(dateString, "YYYY-MM-DD").isValid();
};


export const checkDateValidation = (birthDate: string, deathDate: string) => {
  const currentDate = dayjs();
  const formattedBirthDate = dayjs(birthDate);
  const formattedDeathDate = dayjs(deathDate);

  if (formattedBirthDate.isAfter(currentDate)) {
    return "Birth Date cannot be in the future.";
  }

  if (formattedDeathDate.isAfter(currentDate)) {
    return "Death Date cannot be in the future.";
  }

  if (formattedBirthDate.isAfter(formattedDeathDate)) {
    return "Birth Date should not be greater than Death Date.";
  }

  return null;
};

export default checkDateValidation;
